import { Currency } from 'module/api/account/types';
import { SalesforceAPI } from 'module/api/integrations/salesforce';
import {
  LinkedResource,
  ProjectFinancialsContractType,
} from 'module/api/integrations/salesforce/other-types';
import {
  AutomationError,
  ChildResourcesResponse,
  ContactFieldSyncDetailConfig,
  CreateAutomationPayload,
  ErrorLogs,
  IntegrationField,
  MappableSalesforceFieldsResponse,
  SaveContactFieldSyncPayload,
  SaveContactFieldSyncResponse,
  SalesforceActions,
  SalesforceAutomation,
  SalesforceConfig,
  SalesforceFieldMapping,
  SalesforceIntegrationProvider,
  SalesforceObject,
  SalesforceOrderPayload,
  SalesforceResourceCreatePayload,
  SalesforceResourceMapping,
  SalesforceResourceTypesByCategory,
  SalesforceSettingsMeta,
  SfInstallations,
  UpdateAutomationPayload,
} from 'module/api/integrations/salesforce/types';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const SALESFORCE_CONFIG_QUERY_KEY = ['getSalesforceConfig'];

export const getSfInstallationsQueryKey = (intergationId: number): string[] => [
  `sf-installations-${intergationId}`,
];

export const useSalesforceConfigQuery = (
  options: Omit<
    UseQueryOptions<
      SalesforceConfig[],
      unknown,
      SalesforceConfig[],
      typeof SALESFORCE_CONFIG_QUERY_KEY
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceConfig[], unknown> => {
  return useQuery({
    queryKey: SALESFORCE_CONFIG_QUERY_KEY,
    queryFn: () => SalesforceAPI.getIntegrationConfig(),
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
};

export const useSalesforceConfigByIdQuery = (
  integrationId: number,
  options: Omit<
    UseQueryOptions<
      SalesforceConfig,
      unknown,
      SalesforceConfig,
      [typeof SALESFORCE_CONFIG_QUERY_KEY, number]
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceConfig, unknown> => {
  return useQuery({
    queryKey: [SALESFORCE_CONFIG_QUERY_KEY, integrationId],
    queryFn: () => SalesforceAPI.getIntegrationConfigById(integrationId),
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
};

export const useDisconnectSalesForceMutation = (): UseMutationResult<
  void,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (integrationId: number) => {
      return SalesforceAPI.disconnectIntegration(integrationId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: SALESFORCE_CONFIG_QUERY_KEY,
      });
    },
  });
};

export const SALESFORCE_AUTH_QUERY_KEY = 'salesforceAuthenticationDetails';

export const useSalesForceAuthenticateQuery = (
  options: Omit<
    UseQueryOptions<
      SalesforceIntegrationProvider,
      unknown,
      SalesforceIntegrationProvider,
      [typeof SALESFORCE_AUTH_QUERY_KEY]
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceIntegrationProvider, unknown> => {
  return useQuery({
    queryKey: [SALESFORCE_AUTH_QUERY_KEY],
    queryFn: () => SalesforceAPI.getAuthDetails({ params: {} }),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const SALESFORCE_TRIGGER_OBJECTS_QUERY_KEY = 'salesforceTriggerObjects';

export const useSalesForceTriggerObjectsQuery = (
  integrationId: number,
  options: Omit<
    UseQueryOptions<
      SalesforceObject[],
      unknown,
      SalesforceObject[],
      [typeof SALESFORCE_TRIGGER_OBJECTS_QUERY_KEY, number]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<SalesforceObject[], unknown> => {
  return useQuery({
    queryKey: [SALESFORCE_TRIGGER_OBJECTS_QUERY_KEY, integrationId],
    queryFn: () => SalesforceAPI.getTriggerObjectsV2(integrationId),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...options,
  });
};

export const SALESFORCE_AUTOMATION_TRIGGER_FIELDS_QUERY_KEY =
  'salesforceAutomationTriggerFields';

export const useSalesForceTriggerFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [typeof SALESFORCE_AUTOMATION_TRIGGER_FIELDS_QUERY_KEY, number, string]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery({
    queryKey: [
      SALESFORCE_AUTOMATION_TRIGGER_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
    ],
    queryFn: () =>
      SalesforceAPI.getAutomationTriggerFieldsV2(integrationId, salesforceObject),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...options,
  });
};

export const SALESFORCE_AUTOMATION_MATCHES_FIELDS_QUERY_KEY =
  'salesforceAutomationMatchFields';

export const useSalesForceMatchFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [typeof SALESFORCE_AUTOMATION_MATCHES_FIELDS_QUERY_KEY, number, string]
    >,
    'queryKey' | 'queryFn'
  > & {
    onSuccess?: (data: IntegrationField[]) => void;
    onError?: (error: unknown) => void;
    onSettled?: (
      data: IntegrationField[] | undefined,
      error: unknown | null,
    ) => void;
  } = {},
): UseQueryResult<IntegrationField[], unknown> => {
  const { onSuccess, onError, onSettled, ...queryOptions } = options;

  return useQuery({
    queryKey: [
      SALESFORCE_AUTOMATION_MATCHES_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
    ],
    queryFn: async () => {
      try {
        const data = await SalesforceAPI.getAutomationMatchFieldsV2(
          integrationId,
          salesforceObject,
        );

        if (onSuccess) {
          onSuccess(data);
        }

        return data;
      } catch (error) {
        if (onError) {
          onError(error);
        }

        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...queryOptions,
  });
};

export const SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY =
  'salesforceAutomationActionFields';

export const useSalesForceActionFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  actionType: SalesforceActions,
  triggerObjectParentPlaceholder?: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [
        typeof SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY,
        number,
        string,
        SalesforceActions,
        string | undefined,
      ]
    >,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  const { ...queryOptions } = options;

  return useQuery({
    queryKey: [
      SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
      actionType,
      triggerObjectParentPlaceholder,
    ],
    queryFn: () =>
      SalesforceAPI.getSalesforceActionFieldsV2(
        integrationId,
        salesforceObject,
        actionType,
        triggerObjectParentPlaceholder,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...queryOptions,
  });
};

export const useSalesForceActionFieldsQueryForContact = (
  integrationId: number,
  salesforceObject: string,
  actionType: SalesforceActions,
  relationshipId?: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [
        typeof SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY,
        number,
        string,
        SalesforceActions,
        string?,
      ]
    >,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  const { ...queryOptions } = options;

  return useQuery({
    queryKey: [
      SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
      actionType,
      relationshipId,
    ],
    queryFn: () =>
      SalesforceAPI.getSalesforceActionFieldsV2(
        integrationId,
        salesforceObject,
        actionType,
        relationshipId,
      ),
    retry: 1,
    ...queryOptions,
  });
};

export const SALESFORCE_CONTACT_MANDATORY_FIELDS_QUERY_KEY =
  'salesforceContactMandatoryFields';

export const useSalesForceContactMandatoryFieldsQuery = (
  integrationId: number,
  contactObject: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [typeof SALESFORCE_CONTACT_MANDATORY_FIELDS_QUERY_KEY, number, string]
    >,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery({
    queryKey: [
      SALESFORCE_CONTACT_MANDATORY_FIELDS_QUERY_KEY,
      integrationId,
      contactObject,
    ],
    queryFn: () =>
      SalesforceAPI.getContactMandatoryFields(integrationId, contactObject),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY =
  'salesforceAutomationTemplatePlaceholdersFields';

export const useTemplatePlaceholders = (
  integrationId: number,
  salesforceObject: string,
  templateId: any,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [
        typeof SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
        number,
        string,
        string,
      ]
    >,
    'queryKey' | 'queryFn'
  > & {
    onSuccess?: (data: IntegrationField[]) => void;
    onError?: (error: unknown) => void;
    onSettled?: (
      data: IntegrationField[] | undefined,
      error: unknown | null,
    ) => void;
  } = {},
): UseQueryResult<IntegrationField[], unknown> => {
  const { onSuccess, onError, onSettled, ...queryOptions } = options;

  return useQuery({
    queryKey: [
      SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
      templateId,
    ],
    queryFn: async () => {
      try {
        const data = await SalesforceAPI.getTemplatePlaceholdersV2(
          integrationId.toString(),
          templateId,
          salesforceObject,
        );

        if (onSuccess) {
          onSuccess(data);
        }

        return data;
      } catch (error) {
        if (onError) {
          onError(error);
        }

        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
};

export const SALESFORCE_AUTOMATION_BILLING_METHOD_FINANCIALS_FIELDS_QUERY_KEY =
  'salesforceAutomationfinancialContractTypeFields';

export const useSalesForceFinancialsFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  billingMethod: ProjectFinancialsContractType,
  currencyCode: Currency['currencyCode'],
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [
        typeof SALESFORCE_AUTOMATION_BILLING_METHOD_FINANCIALS_FIELDS_QUERY_KEY,
        number,
        string,
        string,
        string,
      ]
    >,
    'queryKey' | 'queryFn'
  > & {
    onSuccess?: (data: IntegrationField[]) => void;
    onError?: (error: unknown) => void;
    onSettled?: (
      data: IntegrationField[] | undefined,
      error: unknown | null,
    ) => void;
  } = {},
): UseQueryResult<IntegrationField[], unknown> => {
  const { onSuccess, onError, onSettled, ...queryOptions } = options;

  return useQuery({
    queryKey: [
      SALESFORCE_AUTOMATION_BILLING_METHOD_FINANCIALS_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
      billingMethod,
      currencyCode,
    ],
    queryFn: async () => {
      try {
        const data = await SalesforceAPI.getFinancialsFieldsV2(
          integrationId.toString(),
          billingMethod,
          currencyCode,
          salesforceObject,
        );

        if (onSuccess) {
          onSuccess(data);
        }

        return data;
      } catch (error) {
        if (onError) {
          onError(error);
        }

        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
};

export const AUTOMATIONS_LIST_QUERY_KEY = ['salesforceAutomationsList'];

export const useSalesForceAutomationsList = (
  options: Omit<
    UseQueryOptions<
      SalesforceAutomation[],
      unknown,
      SalesforceAutomation[],
      typeof AUTOMATIONS_LIST_QUERY_KEY
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceAutomation[], unknown> => {
  return useQuery({
    queryKey: AUTOMATIONS_LIST_QUERY_KEY,
    queryFn: () => SalesforceAPI.getAutomationsV2(),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const AUTOMATIONS_DETAILS_QUERY_KEY = 'salesforceAutomationsDetails';

export const useSalesForceAutomationsDetails = (
  automationId: number,
  options: Omit<
    UseQueryOptions<
      SalesforceAutomation,
      unknown,
      SalesforceAutomation,
      [typeof AUTOMATIONS_DETAILS_QUERY_KEY, number]
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceAutomation, unknown> => {
  return useQuery({
    queryKey: [AUTOMATIONS_DETAILS_QUERY_KEY, automationId],
    queryFn: () => SalesforceAPI.getAutomationByIdV2(automationId),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...options,
  });
};

export const useCreateAutomationMutation = (): UseMutationResult<
  SalesforceAutomation,
  AxiosError<{ errors: AutomationError[] }>,
  CreateAutomationPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAutomationPayload) => {
      return SalesforceAPI.createAutomationV2(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: AUTOMATIONS_LIST_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
};

export const useUpdateAutomationMutation = (): UseMutationResult<
  SalesforceAutomation,
  AxiosError<{ errors: AutomationError[] }>,
  UpdateAutomationPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateAutomationPayload) => {
      return SalesforceAPI.updateAutomationV2(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: AUTOMATIONS_LIST_QUERY_KEY,
        refetchType: 'all',
      });
      queryClient.refetchQueries({ queryKey: [AUTOMATIONS_DETAILS_QUERY_KEY] });
    },
  });
};

export const useDeleteAutomationMutation = (): UseMutationResult<
  void,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (automationId: number) => {
      return SalesforceAPI.deleteAutomationV2(automationId);
    },
    onSuccess: async () => {
      // Invalidate the list query to refetch updated automation list
      await queryClient.invalidateQueries({
        queryKey: AUTOMATIONS_LIST_QUERY_KEY,
      });
      // Also refetch the details query
      queryClient.refetchQueries({ queryKey: [AUTOMATIONS_DETAILS_QUERY_KEY] });
    },
  });
};

export const useReorderAutomationMutation = (): UseMutationResult<
  SalesforceAutomation[],
  unknown,
  SalesforceOrderPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ automationId, automationUpdatedOrder }) => {
      return SalesforceAPI.reorderAutomationV2(
        automationId,
        automationUpdatedOrder,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: AUTOMATIONS_LIST_QUERY_KEY,
      });
    },
  });
};

export const SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY =
  'salesforceResourceMappings';

export const useSalesForceResourceMappings = (
  integrationId: number,
  nativeResourceType?: 'project' | 'company' | 'user',
  options: Omit<
    UseQueryOptions<
      SalesforceResourceMapping[],
      unknown,
      SalesforceResourceMapping[],
      [typeof SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY, number, string?]
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceResourceMapping[], unknown> =>
  useQuery({
    queryKey: [
      SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY,
      integrationId,
      nativeResourceType,
    ],
    queryFn: () =>
      SalesforceAPI.getResourceMappings(integrationId, nativeResourceType),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_EXTERNAL_RESOURCE_TYPES_QUERY_KEY =
  'salesforceExternalResourceTypes';

export const useSalesForceExternalResourceTypes = (
  integrationId: number,
  options: Omit<
    UseQueryOptions<
      SalesforceResourceTypesByCategory,
      unknown,
      SalesforceResourceTypesByCategory,
      [typeof SALESFORCE_EXTERNAL_RESOURCE_TYPES_QUERY_KEY, number]
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceResourceTypesByCategory, unknown> =>
  useQuery({
    queryKey: [SALESFORCE_EXTERNAL_RESOURCE_TYPES_QUERY_KEY, integrationId],
    queryFn: () => SalesforceAPI.getExternalResourceTypes(integrationId),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_LINK_OBJECT_KEY = 'salesforceLinkObjectKey';

export const useSalesForceLinkObjectQuery = (
  integrationId: number,
  projectId: number,
  salesforceObjectUrl: string,
  options: Omit<
    UseQueryOptions<
      LinkedResource,
      unknown,
      LinkedResource,
      [typeof SALESFORCE_LINK_OBJECT_KEY, number]
    >,
    'queryKey'
  > = {},
) =>
  useQuery({
    queryKey: [SALESFORCE_LINK_OBJECT_KEY, integrationId],
    queryFn: () =>
      SalesforceAPI.linkSalesforceObject(integrationId, {
        projectId,
        sObjectUrl: salesforceObjectUrl,
      }),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
export const SALESFORCE_UN_LINK_OBJECT_KEY = 'salesforceUnLinkObjectKey';

export const useSalesForceUnLinkObjectQuery = (
  integrationId: number,
  resourceMappingId: number,
  options: Omit<
    UseQueryOptions<
      SalesforceObject,
      unknown,
      SalesforceObject,
      [typeof SALESFORCE_LINK_OBJECT_KEY, number]
    >,
    'queryKey'
  > = {},
) =>
  useQuery({
    queryKey: [SALESFORCE_LINK_OBJECT_KEY, integrationId],
    queryFn: () =>
      SalesforceAPI.unLinkSalesforceObjectWithProject(
        integrationId,
        resourceMappingId,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_GET_LINK_OBJECT_KEY = ['salesforceGetLinkObjectKey'];

export const useSalesForceGetLinkObjectQuery = (
  integrationId?: number,
  externalResourceId?: string,
  externalResourceType?: string,
  options: Omit<
    UseQueryOptions<
      SalesforceObject,
      unknown,
      SalesforceObject,
      typeof SALESFORCE_GET_LINK_OBJECT_KEY
    >,
    'queryKey'
  > = {},
) =>
  useQuery({
    queryKey: SALESFORCE_GET_LINK_OBJECT_KEY,
    queryFn: () =>
      SalesforceAPI.getSalesforceObjectWithProject(
        integrationId,
        externalResourceId,
        externalResourceType,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_NATIVE_RESOURCE_TYPES_QUERY_KEY =
  'salesforceNativeResourceTypes';

export const useSalesForceNativeResourceTypes = (
  integrationId: number,
  options: Omit<
    UseQueryOptions<
      SalesforceResourceMapping[],
      unknown,
      SalesforceResourceMapping[],
      [typeof SALESFORCE_NATIVE_RESOURCE_TYPES_QUERY_KEY, number]
    >,
    'queryKey'
  > = {},
): UseQueryResult<SalesforceResourceMapping[], unknown> =>
  useQuery({
    queryKey: [SALESFORCE_NATIVE_RESOURCE_TYPES_QUERY_KEY, integrationId],
    queryFn: () => SalesforceAPI.getNativeResourceTypes(integrationId),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY =
  'salesforceExternalResourceFields';

export const useSalesForceExternalResourceFields = (
  integrationId: number,
  externalResourceType: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [typeof SALESFORCE_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY, number, string]
    >,
    'queryKey'
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery({
    queryKey: [
      SALESFORCE_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      externalResourceType,
    ],
    queryFn: () =>
      SalesforceAPI.getExternalResourceFields(
        integrationId,
        externalResourceType,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_NATIVE_RESOURCE_FIELDS_QUERY_KEY =
  'salesforceNativeResourceFields';

export const useSalesForceNativeResourceFields = (
  integrationId: number,
  nativeResourceType: string,
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [typeof SALESFORCE_NATIVE_RESOURCE_FIELDS_QUERY_KEY, number, string]
    >,
    'queryKey'
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery({
    queryKey: [
      SALESFORCE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      nativeResourceType,
    ],
    queryFn: () =>
      SalesforceAPI.getNativeResourceFields(integrationId, nativeResourceType),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const SALESFORCE_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY =
  'salesforceMappableNativeResourceFields';

export const useSalesForceMappableNativeResourceFields = (
  integrationId: number,
  nativeResourceType: string,
  externalResourceType: string,
  externalResourceFieldId: string,
  fieldMappingConfigId?: number, // Optional parameter
  options: Omit<
    UseQueryOptions<
      IntegrationField[],
      unknown,
      IntegrationField[],
      [
        typeof SALESFORCE_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
        number,
        string,
        string,
        string,
        number | undefined,
      ]
    >,
    'queryKey'
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery({
    queryKey: [
      SALESFORCE_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      nativeResourceType,
      externalResourceType,
      externalResourceFieldId,
      fieldMappingConfigId,
    ],
    queryFn: () =>
      SalesforceAPI.getMappableNativeResourceFieldsV2(
        integrationId,
        nativeResourceType,
        externalResourceType,
        externalResourceFieldId,
        fieldMappingConfigId,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useSalesForceCreateResourceMutation = (
  integrationId: number,
): UseMutationResult<
  SalesforceResourceMapping,
  unknown,
  SalesforceResourceCreatePayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SalesforceResourceCreatePayload) => {
      return SalesforceAPI.createResourceObject(integrationId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY],
      });
    },
  });
};

export const useSalesForceDeleteResourceMutation = (
  integrationId: number,
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resourceId: number) => {
      return SalesforceAPI.deleteResourceObject(integrationId, resourceId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY],
      });
    },
  });
};

export const useSalesForceDeleteMappingMutation = (
  integrationId: number,
): UseMutationResult<
  void,
  unknown,
  { resourceId: number; mappingId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { resourceId: number; mappingId: number }) => {
      return SalesforceAPI.deleteFieldMapping(
        integrationId,
        payload.resourceId,
        payload.mappingId,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY],
      });
    },
  });
};

type CreatePayload = {
  resourceId: number;
  mappingData: SalesforceFieldMapping;
};

export const useSalesforceMappingCreateMutation = (
  integrationId: number,
): UseMutationResult<SalesforceFieldMapping, unknown, CreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePayload) =>
      SalesforceAPI.createFieldMapping(
        integrationId,
        payload.resourceId,
        payload.mappingData,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY],
      });
    },
  });
};

type UpdatePayload = {
  resourceId: number;
  fieldMappingId: number;
  mappingData: SalesforceFieldMapping;
};

export const useSalesforceMappingUpdateMutation = (
  integrationId: number,
): UseMutationResult<SalesforceFieldMapping, unknown, UpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdatePayload) =>
      SalesforceAPI.updateFieldMapping(
        integrationId,
        payload.resourceId,
        payload.fieldMappingId,
        payload.mappingData,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY],
      });
    },
  });
};

export const useGetSfInstallationsQuery = (
  integrationId: number,
  enabled: boolean,
): UseQueryResult<SfInstallations[]> =>
  useQuery({
    queryKey: getSfInstallationsQueryKey(integrationId),
    queryFn: () => SalesforceAPI.getSalesForceInstallations(integrationId),
    enabled,
  });

export const useUpdateSalesforceSettingsMutation = (): UseMutationResult<
  SalesforceSettingsMeta,
  unknown,
  { integrationId: number; payload: SalesforceSettingsMeta }
> => {
  return useMutation({
    mutationFn: ({ integrationId, payload }) =>
      SalesforceAPI.updateSalesforceIntegrationSettings(integrationId, payload),
  });
};

export const SALESFORCE_ERROR_LOGS_QUERY_KEY = 'salesforceErrorLogs';

export const useSalesForceErrorLogsQuery = (
  integrationId: string,
  options: Omit<
    UseQueryOptions<
      ErrorLogs[],
      unknown,
      ErrorLogs[],
      [typeof SALESFORCE_ERROR_LOGS_QUERY_KEY, string]
    >,
    'queryKey'
  > = {},
): UseQueryResult<ErrorLogs[], unknown> => {
  return useQuery({
    queryKey: [SALESFORCE_ERROR_LOGS_QUERY_KEY, integrationId],
    queryFn: () => SalesforceAPI.getSalesForceErrorLogs(integrationId),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useSalesForceErrorLogsCSVMutation = (): UseMutationResult<
  string,
  unknown,
  string
> => {
  return useMutation({
    mutationFn: (integrationId: string) =>
      SalesforceAPI.getSalesForceErrorLogsInCSV(integrationId),
  });
};

export const SALESFORCE_CHILD_RESOURCES_QUERY_KEY = 'salesforceChildResources';

export const useSalesForceChildResourcesQuery = (
  integrationId: number,
  parentObject: string,
  triggerObjectParentPlaceholder?: string,
  options: Omit<
    UseQueryOptions<
      ChildResourcesResponse,
      unknown,
      ChildResourcesResponse,
      [typeof SALESFORCE_CHILD_RESOURCES_QUERY_KEY, number, string, string?]
    >,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<ChildResourcesResponse, unknown> => {
  return useQuery({
    queryKey: [
      SALESFORCE_CHILD_RESOURCES_QUERY_KEY,
      integrationId,
      parentObject,
      triggerObjectParentPlaceholder,
    ],
    queryFn: () =>
      SalesforceAPI.getChildResources(
        integrationId,
        parentObject,
        triggerObjectParentPlaceholder,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const SALESFORCE_CONTACT_FIELD_SYNC_DETAIL_QUERY_KEY =
  'salesforceContactFieldSyncDetail';

/**
 * Query for detailed contact field sync configuration
 * Used in the modal/drawer to show field mapping details
 * Called when user clicks "Done" button
 */
export const useSalesForceContactFieldSyncDetailQuery = (
  integrationId: number,
  contactObject: string,
  options: Omit<
    UseQueryOptions<
      ContactFieldSyncDetailConfig,
      unknown,
      ContactFieldSyncDetailConfig,
      [typeof SALESFORCE_CONTACT_FIELD_SYNC_DETAIL_QUERY_KEY, number, string]
    >,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<ContactFieldSyncDetailConfig, unknown> => {
  return useQuery({
    queryKey: [
      SALESFORCE_CONTACT_FIELD_SYNC_DETAIL_QUERY_KEY,
      integrationId,
      contactObject,
    ],
    queryFn: () =>
      SalesforceAPI.getContactFieldSyncDetail(integrationId, contactObject),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const SALESFORCE_MAPPABLE_FIELDS_QUERY_KEY = 'salesforceMappableFields';

/**
 * Query for mappable Salesforce fields for a specific Rocketlane field
 * Used in contact field sync modal to populate dropdown options
 * Called for each Rocketlane field that needs mapping
 */
export const useSalesForceMappableFieldsQuery = (
  integrationId: number,
  externalResourceType: string,
  nativeResourceType: string,
  nativeFieldName: string,
  options: Omit<
    UseQueryOptions<
      MappableSalesforceFieldsResponse,
      unknown,
      MappableSalesforceFieldsResponse,
      [
        typeof SALESFORCE_MAPPABLE_FIELDS_QUERY_KEY,
        number,
        string,
        string,
        string,
      ]
    >,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<MappableSalesforceFieldsResponse, unknown> => {
  return useQuery({
    queryKey: [
      SALESFORCE_MAPPABLE_FIELDS_QUERY_KEY,
      integrationId,
      externalResourceType,
      nativeResourceType,
      nativeFieldName,
    ],
    queryFn: () =>
      SalesforceAPI.getMappableSalesforceFields(
        integrationId,
        externalResourceType,
        nativeResourceType,
        nativeFieldName,
      ),
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Mutation: Save/Update contact field sync configuration
 * Creates or updates field mappings when user clicks "Sync Field" button
 * Invalidates related queries on success to refetch updated data
 */
export const useSaveContactFieldSyncMutation = (): UseMutationResult<
  SaveContactFieldSyncResponse,
  AxiosError<AutomationError>,
  { integrationId: number; payload: SaveContactFieldSyncPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ integrationId, payload }) =>
      SalesforceAPI.saveContactFieldSync(integrationId, payload),
    onSuccess: (_, variables) => {
      // Invalidate validation query to refetch updated sync status
      queryClient.invalidateQueries({
        queryKey: [
          SALESFORCE_CONTACT_MANDATORY_FIELDS_QUERY_KEY,
          variables.integrationId,
        ],
      });

      // Invalidate detail query to refetch updated field mappings
      queryClient.invalidateQueries({
        queryKey: [
          SALESFORCE_CONTACT_FIELD_SYNC_DETAIL_QUERY_KEY,
          variables.integrationId,
        ],
      });
    },
  });
};
